import type CartPaymentInfo from "~/domains/payment/cart/CartPaymentInfo";
import type CartServiceInfo from "~/domains/payment/cart/CartServiceInfo";
import type DiscountInfo from "../../DiscountInfo";
import type { DiscountInfoType } from "../../DiscountInfoType";
import type ServiceDiscountStrategy from "./ServiceDiscountStrategy";
import { getNdcDiscountDescription, roundPrice } from "~/utils/payment";

export default class NdcReduceServiceDiscountStrategy implements ServiceDiscountStrategy {
  getDiscountInfoType(): DiscountInfoType {
    return "ndcReduce";
  }

  getDiscountInfo(cartServiceInfo: CartServiceInfo, cartPaymentInfo: CartPaymentInfo): DiscountInfo {
    const discountInfo: DiscountInfo = {
      type: "ndcReduce",
      description: "",
      value: 0,
      amount: 0,
    };

    const ndcInfo = cartPaymentInfo.ndc;

    /* Reduce only applies if:
      - Has either the defined extra service (priority) or the main service in their cart.
      - This service is a seasonal service.
    */
    if (
      ndcInfo?.pricingRule === "Reduce"
      && (ndcInfo.product?.extraService?.id ?? ndcInfo.product?.id) === cartServiceInfo.ServiceDefId
      && !cartServiceInfo.isMonthly
      && !cartServiceInfo.isInitialPayment
    ) {
      const discountValue = roundPrice(ndcInfo.priceRuleValue ?? 0, "nearest", 2);

      discountInfo.description = `${getNdcDiscountDescription(ndcInfo)} - ${cartServiceInfo.ServiceName}`;
      discountInfo.value = discountValue;
      discountInfo.amount = discountValue;
      discountInfo.serviceDefId = cartServiceInfo.ServiceDefId;
    }

    return discountInfo;
  }

  getMonthlyDiscountInfo(cartServiceInfo: CartServiceInfo, cartPaymentInfo: CartPaymentInfo): DiscountInfo {
    const discountInfo: DiscountInfo = {
      type: "ndcReduce",
      description: "",
      value: 0,
      amount: 0,
    };

    const ndcInfo = cartPaymentInfo.ndc;

    /* Reduce only applies if:
      - Has either the defined extra service (priority) or the main service in their cart.
      - This service is a monthly service.
    */
    if (
      ndcInfo?.pricingRule === "Reduce"
      && (ndcInfo.product?.extraService?.id ?? ndcInfo.product?.id) === cartServiceInfo.ServiceDefId
      && cartServiceInfo.isMonthly
      && !cartServiceInfo.isInitialPayment
    ) {
      const discountValue = roundPrice(ndcInfo.priceRuleValue ?? 0, "nearest", 2);

      discountInfo.description = `${getNdcDiscountDescription(ndcInfo)} - ${cartServiceInfo.ServiceName}`;
      discountInfo.value = discountValue;
      discountInfo.amount = discountValue;
    }

    return discountInfo;
  }

  getInitialPaymentDiscountInfo(cartServiceInfo: CartServiceInfo, cartPaymentInfo: CartPaymentInfo): DiscountInfo {
    const discountInfo: DiscountInfo = {
      type: "ndcReduce",
      description: "",
      value: 0,
      amount: 0,
    };

    const ndcInfo = cartPaymentInfo.ndc;

    /* Reduce only applies if:
      - Has either the defined extra service (priority) or the main service in their cart.
      - This service is an initial payment.
    */
    if (
      ndcInfo?.pricingRule === "Reduce"
      && (ndcInfo.product?.extraService?.id ?? ndcInfo.product?.id) === cartServiceInfo.ServiceDefId
      && !cartServiceInfo.isMonthly
      && cartServiceInfo.isInitialPayment
    ) {
      const discountValue = roundPrice(ndcInfo.priceRuleValue ?? 0, "nearest", 2);

      discountInfo.description = `${getNdcDiscountDescription(ndcInfo)} - ${cartServiceInfo.ServiceName}`;
      discountInfo.value = discountValue;
      discountInfo.amount = discountValue;
    }

    return discountInfo;
  }
}
